import { useEffect, useRef, useState } from 'react'
import {
  Cloud, Clock, Users, Box, Route,
  CheckCircle2, Loader2, X, ChevronRight,
  TrendingUp, Timer, UserCheck, ShieldCheck,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { TOWING_PLAN, TOWING_SUMMARY, PLANNING_STEPS } from '../data/towingPlan'
import type { TowStep } from '../data/towingPlan'
import type { DemoState } from '../hooks/useDemoMode'

interface AutostackerSidebarProps {
  demoState: DemoState
  completedStepIds: string[]
  onPlanningComplete: () => void
  onSelectStep: (step: TowStep) => void
  onClose: () => void
}

const STEP_ICONS = {
  cloud: Cloud,
  timeline: Clock,
  users: Users,
  cube: Box,
  route: Route,
} as const

export function AutostackerSidebar({
  demoState,
  completedStepIds,
  onPlanningComplete,
  onSelectStep,
  onClose,
}: AutostackerSidebarProps) {
  const [currentPlanningStep, setCurrentPlanningStep] = useState(0)
  const [completedPlanningSteps, setCompletedPlanningSteps] = useState<number[]>([])

  const isVisible = demoState === 'planning' || demoState === 'result' || demoState === 'animating'

  // Keep a stable ref to the callback so we don't need it in deps
  const onPlanningCompleteRef = useRef(onPlanningComplete)
  onPlanningCompleteRef.current = onPlanningComplete

  // Drive the progress animation when state = planning.
  // All timers are tracked and cleared on cleanup to prevent orphaned timers
  // (important in React StrictMode which double-invokes effects in development).
  useEffect(() => {
    if (demoState !== 'planning') return

    setCurrentPlanningStep(0)
    setCompletedPlanningSteps([])

    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []

    let stepIndex = 0
    const tick = () => {
      if (cancelled) return
      if (stepIndex >= PLANNING_STEPS.length) {
        const t = setTimeout(() => {
          if (!cancelled) onPlanningCompleteRef.current()
        }, 400)
        timers.push(t)
        return
      }
      const step = PLANNING_STEPS[stepIndex]
      setCurrentPlanningStep(stepIndex)
      const t = setTimeout(() => {
        if (cancelled) return
        setCompletedPlanningSteps((prev) => [...prev, stepIndex])
        stepIndex++
        tick()
      }, step.durationMs)
      timers.push(t)
    }

    const initialTimer = setTimeout(tick, 200)
    timers.push(initialTimer)

    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
    }
  }, [demoState])

  if (!isVisible) return null

  const isPlanning = demoState === 'planning'
  const isResult = demoState === 'result' || demoState === 'animating'

  return (
    <div
      className={cn(
        'absolute right-4 top-1/2 -translate-y-1/2 z-30 w-80',
        'bg-background/97 backdrop-blur-md border rounded-2xl shadow-2xl',
        'flex flex-col max-h-[calc(100vh-6rem)] overflow-hidden',
        'transition-all duration-300 ease-out',
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <div className={cn(
            'w-2 h-2 rounded-full',
            isPlanning ? 'bg-blue-500 animate-pulse' : 'bg-green-500'
          )} />
          <span className="font-semibold text-sm">
            {isPlanning ? '5D AutoStacker' : 'Stacking Plan Ready'}
          </span>
          {isResult && (
            <Badge variant="secondary" className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-0">
              Optimized
            </Badge>
          )}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Planning progress */}
      {isPlanning && (
        <div className="px-4 py-5 space-y-3">
          <p className="text-xs text-muted-foreground mb-4">
            Analyzing {8} parked aircraft across time, space, and kinematics…
          </p>
          {PLANNING_STEPS.map((step, i) => {
            const Icon = STEP_ICONS[step.icon as keyof typeof STEP_ICONS] ?? Loader2
            const isDone = completedPlanningSteps.includes(i)
            const isActive = currentPlanningStep === i && !isDone
            return (
              <div
                key={step.label}
                className={cn(
                  'flex items-center gap-3 transition-all duration-300',
                  isDone ? 'opacity-100' : isActive ? 'opacity-100' : 'opacity-30'
                )}
              >
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center shrink-0',
                  isDone ? 'bg-green-100 dark:bg-green-900 text-green-600' :
                  isActive ? 'bg-blue-100 dark:bg-blue-900 text-blue-600' :
                  'bg-muted text-muted-foreground'
                )}>
                  {isDone ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : isActive ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Icon className="w-3.5 h-3.5" />
                  )}
                </div>
                <span className={cn('text-sm', isDone && 'text-foreground', isActive && 'text-foreground font-medium')}>
                  {step.label}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Result: Summary cards */}
      {isResult && (
        <div className="overflow-y-auto flex-1">
          <div className="px-4 py-3 grid grid-cols-2 gap-2 border-b">
            <SummaryCard
              icon={Route}
              label="Tow Moves"
              value={String(TOWING_SUMMARY.totalMoves)}
              color="blue"
            />
            <SummaryCard
              icon={Timer}
              label="Est. Time"
              value={`${TOWING_SUMMARY.totalMinutes} min`}
              color="purple"
            />
            <SummaryCard
              icon={UserCheck}
              label="Crew Needed"
              value={`${TOWING_SUMMARY.crewRequired} staff`}
              color="orange"
            />
            <SummaryCard
              icon={TrendingUp}
              label="Revenue"
              value={`$${(TOWING_SUMMARY.totalRevenue / 1000).toFixed(1)}k`}
              color="green"
            />
          </div>

          {/* Safety note */}
          <div className="px-4 py-2 flex items-center gap-2 border-b">
            <ShieldCheck className="w-4 h-4 text-green-500 shrink-0" />
            <span className="text-xs text-muted-foreground">
              Min. {TOWING_SUMMARY.minClearanceFt}ft clearance · Risk: {TOWING_SUMMARY.riskLevel}
            </span>
          </div>

          {/* Tow step list */}
          <div className="px-4 py-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Tow Sequence
            </p>
            {TOWING_PLAN.map((step) => {
              const isCompleted = completedStepIds.includes(step.id)
              return (
                <TowStepCard
                  key={step.id}
                  step={step}
                  isCompleted={isCompleted}
                  onClick={() => !isCompleted && onSelectStep(step)}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────

interface SummaryCardProps {
  icon: React.ElementType
  label: string
  value: string
  color: 'blue' | 'green' | 'orange' | 'purple'
}

const colorMap = {
  blue:   'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
  green:  'bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400',
  orange: 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400',
  purple: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400',
}

function SummaryCard({ icon: Icon, label, value, color }: SummaryCardProps) {
  return (
    <div className={cn('rounded-lg px-3 py-2.5', colorMap[color])}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</span>
      </div>
      <p className="text-lg font-bold leading-none">{value}</p>
    </div>
  )
}

interface TowStepCardProps {
  step: TowStep
  isCompleted: boolean
  onClick: () => void
}

function TowStepCard({ step, isCompleted, onClick }: TowStepCardProps) {
  const typeColors = {
    hold: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    reposition: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    park: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  }

  return (
    <button
      onClick={onClick}
      disabled={isCompleted}
      className={cn(
        'w-full text-left rounded-xl border p-3 transition-all',
        isCompleted
          ? 'opacity-50 bg-muted/30 border-transparent'
          : 'hover:bg-accent hover:border-accent-foreground/10 border-border cursor-pointer'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {isCompleted ? (
            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
          ) : (
            <span className="w-4 h-4 rounded-full bg-muted text-muted-foreground text-[10px] font-bold flex items-center justify-center mt-0.5 shrink-0">
              {step.stepNumber}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold truncate">{step.aircraftLabel}</p>
            <p className="text-xs text-muted-foreground leading-snug mt-0.5">{step.description}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded', typeColors[step.type])}>
            {step.type}
          </span>
          <span className="text-[10px] text-muted-foreground">{step.durationMinutes}m</span>
        </div>
      </div>
      {!isCompleted && (
        <div className="flex items-center gap-1 mt-2 text-[10px] text-primary font-medium">
          <Route className="w-3 h-3" />
          Click to show sweep path
          <ChevronRight className="w-3 h-3 ml-auto" />
        </div>
      )}
    </button>
  )
}
